import { Link, useParams } from "@tanstack/react-router";
import { MdRoundBlock, MdRoundCheckCircle, MdRoundError, MdRoundHelp, MdRoundRefresh } from "@teamworksdev/icons";
import { Button, cn, Heading, Pill } from "@teamworksdev/react";
import { Card } from "~/components";
import { sortingFns } from "~/utils/sorting";
import { getCategoryDisplayName, TOOLS_BY_CATEGORY } from "./constants";
import type { useToolHealthCheck } from "./hooks";
import { getToolNavigation } from "./navigation";
import type { HealthStatus, SupportTool, SupportToolsListProps } from "./types";

// Health status pill variants
const HEALTH_STATUS_VARIANTS: Record<
  HealthStatus,
  { type: "success" | "error" | "info" | "default"; label: string } | null
> = {
  healthy: { type: "success", label: "Online" },
  unhealthy: { type: "error", label: "Offline" },
  disabled: { type: "default", label: "Disabled" },
  checking: { type: "info", label: "Checking..." },
  unknown: null,
};

const getHealthStatusBadge = (status: HealthStatus) => {
  const config = HEALTH_STATUS_VARIANTS[status];
  return config ? <Pill type={config.type}>{config.label}</Pill> : null;
};

// Health status item configuration
interface HealthStatusItemConfig {
  icon: React.ComponentType<{ className?: string }>;
  iconClassName: string;
  label: string;
  statusKey: HealthStatus;
  showWhenZero?: boolean;
}

const HealthStatusItem = ({ icon: Icon, iconClassName, label, count }: HealthStatusItemConfig & { count: number }) => (
  <div className="flex items-center gap-1">
    <Icon className={`h-4 w-4 ${iconClassName}`} />
    <span className="text-black/mid-emphasis">
      {count} {label}
    </span>
  </div>
);

const HEALTH_STATUS_ITEMS: HealthStatusItemConfig[] = [
  {
    icon: MdRoundCheckCircle,
    iconClassName: "text-success",
    label: "healthy",
    statusKey: "healthy",
  },
  {
    icon: MdRoundError,
    iconClassName: "text-error",
    label: "offline",
    statusKey: "unhealthy",
  },
  {
    icon: MdRoundBlock,
    iconClassName: "text-warning",
    label: "disabled",
    statusKey: "disabled",
  },
  {
    icon: MdRoundHelp,
    iconClassName: "text-black/low-emphasis",
    label: "unknown",
    statusKey: "unknown",
  },
  {
    icon: MdRoundRefresh,
    iconClassName: "text-blue-600 animate-spin",
    label: "checking",
    statusKey: "checking",
    showWhenZero: false,
  },
];

const getHealthStatusIcon = (status: HealthStatus) => {
  const statusItem = HEALTH_STATUS_ITEMS.find((item) => item.statusKey === status);
  if (!statusItem) return null;
  const Icon = statusItem.icon;
  return <Icon className={`h-5 w-5 ${statusItem.iconClassName}`} />;
};

interface SupportToolsListWithHealthProps extends SupportToolsListProps {
  healthCheckHook: ReturnType<typeof useToolHealthCheck>;
}

export const SupportToolsList = ({ healthCheckHook, showHeader = true }: SupportToolsListWithHealthProps) => {
  // Use shared health check hook
  const { getToolHealthStatus, refreshHealthChecks, healthStats, isCheckingAny, toolsWithHealthCheck } =
    healthCheckHook;

  return (
    <div className={cn("h-full flex flex-col gap-8 md:flex", { "py-4": showHeader })}>
      {showHeader && (
        <div>
          <Heading as="h1">Support tools</Heading>
          <p className="text-black/mid-emphasis">Support and administrative tools for managing Teamworks products</p>
        </div>
      )}

      {toolsWithHealthCheck.length > 0 && (
        <Card className="p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Heading as="h2" size="h2" className="text-sm font-medium text-black/high-emphasis">
                Service status
              </Heading>
              <div className="flex items-center gap-3 text-sm">
                {HEALTH_STATUS_ITEMS.map((item) => {
                  const count = healthStats[item.statusKey];
                  if (count === 0 && item.showWhenZero === false) return null;
                  return <HealthStatusItem key={item.label} {...item} count={count} />;
                })}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-xs text-black/mid-emphasis">
                Having trouble? Ask in{" "}
                <a
                  href="https://teamworkschat.slack.com/archives/C09J924MF9N"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-black/high-emphasis"
                >
                  #support-tools-questions
                </a>
                .
              </p>
              <Button isLoading={isCheckingAny} size="sm" variant="secondary" onClick={refreshHealthChecks}>
                Check status
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-8">
        {Object.entries(TOOLS_BY_CATEGORY)
          .sort(([categoryA], [categoryB]) => {
            // Always put 'support' category last
            if (categoryA === "support") return 1;
            if (categoryB === "support") return -1;

            // Sort alphabetically for all other categories using alphanumeric sorting
            return sortingFns.alphanumeric()(categoryA, categoryB);
          })
          .map(([category, tools]) => {
            if (tools.length === 0) return null;

            return (
              <div key={category}>
                <Heading as="h3" size="h2" className="text-xl font-semibold text-black/high-emphasis mb-4">
                  {getCategoryDisplayName(category)}
                </Heading>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tools.map((tool) => {
                    const healthStatus = getToolHealthStatus(tool.id);
                    const isDisabled = healthStatus === "disabled" || healthStatus === "unhealthy";
                    return (
                      <SupportToolCard
                        key={tool.id}
                        tool={tool}
                        isDisabled={isDisabled}
                        healthStatusIcon={
                          tool.healthEndpoint ? (
                            <div className="flex items-center gap-1">{getHealthStatusIcon(healthStatus)}</div>
                          ) : null
                        }
                        healthStatusBadge={getHealthStatusBadge(healthStatus)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

interface SupportToolCardProps {
  tool: SupportTool;
  isDisabled: boolean;
  healthStatusIcon: React.ReactNode;
  healthStatusBadge: React.ReactNode;
}

const SupportToolCard = ({ tool, isDisabled, healthStatusIcon, healthStatusBadge }: SupportToolCardProps) => {
  const { orgId } = useParams({ strict: false });
  const isOrgContext = !!orgId;

  // Generate navigation path
  const navigation = getToolNavigation(tool.id, isOrgContext && orgId ? orgId : undefined);

  const cardContent = (
    <Card className={cn("h-full", { "opacity-50": isDisabled })}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {tool.icon && (
              <div className="text-2xl" title={tool.name}>
                {tool.icon}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Heading size="h3" className="text-lg font-semibold text-black/high-emphasis">
                {tool.name}
              </Heading>
              {healthStatusIcon}
            </div>
          </div>
          {tool.healthEndpoint && healthStatusBadge}
        </div>
        <p className="text-sm text-black/mid-emphasis">{tool.description}</p>
      </div>
    </Card>
  );

  if (isDisabled) {
    return <div className="block h-full cursor-not-allowed">{cardContent}</div>;
  }

  return (
    <Link
      to={navigation.to}
      params={navigation.params}
      className="block h-full transition-all duration-200 cursor-pointer hover:shadow-md"
    >
      {cardContent}
    </Link>
  );
};
